<template>
  <div class="screenshots-tab">
    <div class="view-controls">
      <button
        :class="{ active: viewMode === 'carousel' }"
        @click="setViewMode('carousel')"
        class="view-button"
      >
        <span class="material-symbols-outlined">view_carousel</span>
        <span class="view-label">Carousel View</span>
      </button>

      <div class="divider"></div>

      <button
        :class="{ active: viewMode === 'grid' }"
        @click="setViewMode('grid')"
        class="view-button"
      >
        <span class="material-symbols-outlined">view_agenda</span>
        <span class="view-label">Grid View</span>
      </button>
    </div>

    <div class="screenshots-container" v-if="screenshots.hasScreenshots.status">
      <button
        class="nav-button prev"
        @click="scroll('left')"
        v-show="canScrollLeft && viewMode === 'carousel'"
      >
        <span class="material-symbols-outlined">chevron_left</span>
      </button>

      <div
        class="screenshots-wrapper"
        ref="scrollContainer"
        :class="{ 'grid-view': viewMode === 'grid' }"
        @mousedown="startDragging"
        @mousemove="drag"
        @mouseup="stopDragging"
        @mouseleave="stopDragging"
      >
        <div
          v-for="(screenshot, key) in screenshots.hasScreenshots.screenShots"
          :key="key"
          class="screenshot-item"
          :class="{ 'grid-item': viewMode === 'grid' }"
          @click="openPreview(screenshot.imgPath)"
        >
          <div class="screenshot-wrapper">
            <img
              :src="screenshot.imgPath"
              :key="screenshot.imgPath"
              :alt="'Screenshot ' + key"
              class="screenshot"
            />
            <div class="preview-button">
              <span>{{ $t("Apps.clickToPreview") }}</span>
            </div>
          </div>
        </div>
      </div>

      <button
        class="nav-button next"
        @click="scroll('right')"
        v-show="canScrollRight && viewMode === 'carousel'"
      >
        <span class="material-symbols-outlined">chevron_right</span>
      </button>
    </div>

    <div class="preview-modal" v-if="previewImage" @click="closePreview">
      <div class="modal-content">
        <button class="close-button" @click="closePreview">
          <span class="material-symbols-outlined">close</span>
        </button>
        <img :src="previewImage" alt="Preview" />
      </div>
    </div>
  </div>
</template>

<script>
import { showScreenshot } from "@/api/custom.api";

export default {
  name: "ScreenshotsTab",
  props: {
    screenshots: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      previewImage: null,
      canScrollLeft: false,
      canScrollRight: true,
      isDragging: false,
      startX: 0,
      scrollLeft: 0,
      clickStartTime: 0,
      viewMode: "carousel",
    };
  },
  mounted() {
    this.checkScroll();
    this.$refs.scrollContainer.addEventListener("scroll", this.checkScroll);
  },
  beforeDestroy() {
    if (this.$refs.scrollContainer) {
      this.$refs.scrollContainer.removeEventListener(
        "scroll",
        this.checkScroll
      );
    }
  },
  methods: {
    startDragging(e) {
      this.isDragging = true;
      this.clickStartTime = Date.now();
      this.startX = e.pageX - this.$refs.scrollContainer.offsetLeft;
      this.scrollLeft = this.$refs.scrollContainer.scrollLeft;
      this.$refs.scrollContainer.style.cursor = "grabbing";
      this.$refs.scrollContainer.style.userSelect = "none";
    },
    drag(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const x = e.pageX - this.$refs.scrollContainer.offsetLeft;
      const walk = (x - this.startX) * 2;
      this.$refs.scrollContainer.scrollLeft = this.scrollLeft - walk;
    },
    stopDragging(e) {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.$refs.scrollContainer.style.cursor = "grab";
      this.$refs.scrollContainer.style.removeProperty("user-select");

      if (Date.now() - this.clickStartTime < 200) {
        const target = e.target.closest(".screenshot-item");
        if (target) {
          const imgSrc = target.querySelector("img").src;
          this.openPreview(imgSrc);
        }
      }
    },
    openPreview(imgPath) {
      if (!this.isDragging) {
        this.previewImage = imgPath;
        document.body.style.overflow = "hidden";
      }
    },
    closePreview() {
      this.previewImage = null;
      document.body.style.overflow = "";
    },
    scroll(direction) {
      const container = this.$refs.scrollContainer;
      const scrollAmount = 300;
      if (direction === "left") {
        container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      } else {
        container.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    },
    checkScroll() {
      const container = this.$refs.scrollContainer;
      this.canScrollLeft = container.scrollLeft > 0;
      this.canScrollRight =
        container.scrollLeft < container.scrollWidth - container.clientWidth;
    },
    setViewMode(mode) {
      this.viewMode = mode;
      this.$nextTick(() => {
        this.checkScroll();
      });
    },
  },
};
</script>

<style scoped>
.screenshots-view-controls {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
  margin-right: 15px;
}

.screenshots-view-controls button {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 8px;
  margin: 0 4px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.screenshots-view-controls button:hover {
  background-color: #f0f0f0;
}

.screenshots-view-controls button.active {
  background-color: #e0e0e0;
  color: var(--logo-color);
}

.view-mode-text {
  font-size: 14px;
  color: var(--logo-color);
}

.screenshots-wrapper.grid-view {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(213px, 1fr));
  grid-gap: 20px;
  overflow-x: hidden;
  padding: 10px;
  cursor: default;
}


.screenshots-tab {
  padding: 0;
  position: relative;
}

.screenshots-container {
  position: relative;
}

.screenshots-wrapper {
  display: flex;
  gap: 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 10px 0;
  cursor: grab;
  user-select: none;
}

.screenshots-wrapper::-webkit-scrollbar {
  display: none;
}

.screenshots-wrapper:active {
  cursor: grabbing;
}

.screenshot-item {
  flex: 0 0 300px;
  position: relative;
}

/* .screenshot-wrapper {
  position: relative;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  /* aspect-ratio: 9/16; 
}*/

.screenshot-wrapper:hover {
  transform: translateY(-4px);
}

.screenshot-wrapper:hover .preview-button {
  opacity: 1;
}

.screenshot-item img {
  border-radius: 15px;
  width: 100%;
  height: auto;
  object-fit: cover;
  pointer-events: none;
}

.preview-button {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
    user-select: none;
    white-space: nowrap;
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  transition: background-color 0.3s ease;
}

.nav-button:hover {
  background-color: #f5f5f5;
}

.nav-button.prev {
  left: 0;
}

.nav-button.next {
  right: 0;
}

.preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.modal-content img {
  max-width: 100%;
  min-height: 55vh;
  overflow: hidden;
  max-height: 90vh;
  object-fit: contain;
}

.close-button {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
}

.close-button .material-symbols-outlined {
  font-size: 32px;
}

/** */
.view-controls {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  float: inline-end;
  background: #ffffff;
  padding: 6px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
}

.view-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
}

.view-button:hover {
  background: #f8fafc;
}

.view-button.active {
  background: var(--logo-color);
  color: white;
}

.view-button.active:hover {
  background:  var(--logo-color);
}

.view-button .material-symbols-outlined {
  font-size: 20px;
}

.view-label {
  font-size: 14px;
  font-weight: 500;
}

.divider {
  width: 1px;
  height: 24px;
  background: #e2e8f0;
}

</style>
